
import { createContext, useContext, useState, ReactNode, useRef, useEffect } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CartItem } from "@/contexts/CartContext";

interface PendingCartItem extends Omit<CartItem, 'quantity'> {
  quantity?: number;
}

interface SignUpData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

type AuthContextType = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAuthModalOpen: boolean;
  pendingCartItem: PendingCartItem | null;
  signIn: (email: string, password: string) => Promise<{
    error: Error | null;
  }>;
  signUp: (data: SignUpData, fromModal?: boolean) => Promise<{
    error: Error | null;
    data: any;
    isNewUser?: boolean;
  }>;
  signOut: () => Promise<void>;
  setAuthModalOpen: (open: boolean) => void;
  setPendingCartItem: (item: PendingCartItem | null) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Storage keys for redirect flags
const REDIRECT_HANDLED_KEY = 'auth_redirect_handled';
const IS_NEW_USER_KEY = 'is_new_user';

interface AuthProviderProps {
  children: ReactNode;
  addItemToCart?: (item: CartItem) => void;
}

export function AuthProvider({ children, addItemToCart }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
  const [pendingCartItem, setPendingCartItem] = useState<PendingCartItem | null>(null);
  const { toast } = useToast();
  const isRedirecting = useRef(false);

  // Initialize auth state
  useEffect(() => {
    // Clear any stale redirect flags when component mounts
    try {
      sessionStorage.removeItem(REDIRECT_HANDLED_KEY);
    } catch (error) {
      console.error("Error accessing sessionStorage:", error);
    }
    
    // First set up the auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("Auth event:", event);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsLoading(false);
        
        if ((event === 'SIGNED_IN' || event === 'USER_UPDATED') && currentSession) {
          try {
            // Avoid redirecting if we're already in the process of doing so
            if (isRedirecting.current) {
              console.log("Already redirecting, skipping additional navigation");
              return;
            }
            
            // Check if we've already handled a redirect for this auth event
            const redirectHandled = sessionStorage.getItem(REDIRECT_HANDLED_KEY);
            const isNewUser = sessionStorage.getItem(IS_NEW_USER_KEY) === 'true';
            console.log("Auth redirect check - handled:", redirectHandled, "isNewUser:", isNewUser);
            
            // Handle any pending cart items first
            if (pendingCartItem && addItemToCart) {
              setTimeout(() => {
                addItemToCart({
                  ...pendingCartItem,
                  quantity: pendingCartItem.quantity || 1
                });
                
                toast({
                  title: "Item added to cart",
                  description: `${pendingCartItem.name} was added to your cart`
                });
                
                // Clear the pending item
                setPendingCartItem(null);
              }, 0);
            }
            
            // Only redirect if we haven't already handled it
            if (!redirectHandled) {
              isRedirecting.current = true;
              
              // Set a flag to prevent duplicate redirect attempts
              sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'true');
              
              if (isNewUser) {
                console.log("New user detected, redirecting to onboarding");
                window.location.href = '/onboarding';
              } else {
                // If not a new user and not on auth page, don't redirect
                if (window.location.pathname === '/auth') {
                  console.log("Existing user signed in from auth page, redirecting to home");
                  window.location.href = '/';
                }
              }
            }
          } catch (error) {
            console.error("Error handling auth redirect:", error);
          } finally {
            // Reset the redirecting flag after a short delay to prevent issues
            setTimeout(() => {
              isRedirecting.current = false;
            }, 1000);
          }
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [pendingCartItem, addItemToCart, toast]);

  const signIn = async (email: string, password: string) => {
    try {
      // Clear any stale flags
      sessionStorage.removeItem(IS_NEW_USER_KEY);
      sessionStorage.removeItem(REDIRECT_HANDLED_KEY);
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      // Mark that this is NOT a new user (it's a sign-in)
      sessionStorage.setItem(IS_NEW_USER_KEY, 'false');
      
      return { error: null };
    } catch (error) {
      console.error("Error signing in:", error);
      toast({
        title: "Sign in failed",
        description: (error as Error).message,
        variant: "destructive",
      });
      return { error: error as Error };
    }
  };

  const signUp = async ({ email, password, firstName, lastName }: SignUpData, fromModal = false) => {
    try {
      // Clear any stale flags before starting signup
      sessionStorage.removeItem(IS_NEW_USER_KEY);
      sessionStorage.removeItem(REDIRECT_HANDLED_KEY);
      
      console.log("Starting sign up process, cleared previous flags");
      
      // Include first and last names in the user metadata
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName || '',
            last_name: lastName || '',
          },
          emailRedirectTo: `${window.location.origin}/auth?confirmation=true`,
        }
      });
      
      if (error) throw error;
      
      // Explicitly mark as a new user BEFORE any redirects happen
      console.log("Sign up successful, marking as new user");
      sessionStorage.setItem(IS_NEW_USER_KEY, 'true');
      
      // If signing up from modal, handle redirect differently
      if (fromModal) {
        setIsAuthModalOpen(false);
      }
      
      let toastMessage = "Check your email to confirm your account";
      
      // If we have an immediate session, we're already logged in
      if (data.session) {
        toastMessage = "Your account has been created. Redirecting to onboarding...";
        // The redirect will be handled by onAuthStateChange
        // Just ensure the flag is set
        sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'false');
      }
      
      toast({
        title: "Account created",
        description: toastMessage,
      });
      
      return { data, error: null, isNewUser: true };
    } catch (error) {
      console.error("Error signing up:", error);
      toast({
        title: "Sign up failed",
        description: (error as Error).message,
        variant: "destructive",
      });
      return { data: null, error: error as Error, isNewUser: false };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Signed out successfully",
      });
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Error signing out",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        isLoading,
        isAuthModalOpen,
        pendingCartItem,
        signIn,
        signUp,
        signOut,
        setAuthModalOpen: setIsAuthModalOpen,
        setPendingCartItem,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
